import * as React from "react"
import { ArrowDown, ArrowUp, ChevronRight, ChevronsUpDown, Columns3, RotateCcw } from "lucide-react"

import { cn } from "@/lib/utils"
import { formatNumber } from "@/lib/format"
import { groupRows, nextSort, sortRows } from "@/lib/table"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"
import { TableCheckbox } from "@/components/ui/table-checkbox"
import { BulkBar } from "@/components/ui/bulk-bar"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"

/*
  DataTable — the list table of the overhaul (kit `table.t`): sticky header under the app top bar,
  sortable columns, right-aligned tabular numbers, row selection feeding a floating BulkBar, keyboard
  row focus (↑/↓, Home/End, Enter opens, Space selects), group header rows that collapse, expandable
  rows, compact density, skeleton rows while loading, an EmptyState, and incremental rendering
  (the first `initialRowCount` rows + "Show more", grouped lists included) so 2,000-row lists stay
  fast. Docs: UI_FOUNDATION.md.

    const columns = useMemo(() => [
      { key: 'name', header: 'Project', sortable: true, render: (p) => <ProjectCell project={p} /> },
      { key: 'value', header: 'Value', numeric: true, sortable: true, width: 140, render: (p) => formatMoney(p.value, { currency: null }) },
      { key: 'due', header: 'Due', sortable: true, sortValue: (p) => p.end_date, defaultHidden: true },
      { key: 'actions', header: '', width: 52, hideable: false, render: (p) => <RowActions items={…} /> },
    ], [deps]);
    const columnState = useColumnVisibility('projects', columns);
    <DataTable columns={columns} columnVisibility={columnState} rows={projects} rowKey="id"
      defaultSort={{ key: 'due', dir: 'asc' }} selectable selectedKeys={selected} onSelectionChange={setSelected}
      bulkActions={(rows) => [{ label: 'Set status', icon: Flag, onSelect: () => openStatus(rows) }]}
      onRowOpen={(p) => navigate(`/projects/${p.id}`)} groupBy={(p) => ({ key: p.site, label: p.site })} />

  Column: { key, header (node), title (menu label), align ('left'|'right'|'center'), numeric (right +
  tabular figures), width, minWidth, sortable, sortValue(row), sortKey, defaultSortDir ('asc'|'desc'),
  render(row, ctx), cellClassName, headerClassName, hideable, defaultHidden, truncate, sortHint (always show the
  ↕ hint on this sortable header — `showSortHint` does it for every column) }.
  ctx = { index, selected, expanded, toggleSelected(), toggleExpanded() }.
  See docs/UI_FOUNDATION.md "Wave 2 additions" for every prop.
*/

const INTERACTIVE_SELECTOR = 'a, button, input, select, textarea, label, summary, [role="checkbox"], [role="menuitem"], [role="option"], [contenteditable="true"], [data-row-click="ignore"]'

function readKey(row, rowKey, index) {
  if (typeof rowKey === "function") return rowKey(row, index)
  return row == null ? index : row[rowKey]
}

function alignOf(column) {
  return column.align || (column.numeric ? "right" : "left")
}

function alignClass(column) {
  const align = alignOf(column)
  return cn(align === "right" && "text-right", align === "center" && "text-center", column.numeric && "tabular-nums")
}

function sortValueOf(columns) {
  const byKey = new Map(columns.map((column) => [column.key, column]))
  return (row, key) => {
    const column = byKey.get(key)
    if (column?.sortValue) return column.sortValue(row)
    return row == null ? undefined : row[column?.sortKey || key]
  }
}

function DefaultCell({ value }) {
  if (value === null || value === undefined || value === "") {
    return (
      <>
        <span className="text-ink-4" aria-hidden="true">—</span>
        <span className="sr-only">None</span>
      </>
    )
  }
  return <>{String(value)}</>
}

/* Window-sticky header needs no scrolling ancestor; when the columns do not fit, the table scrolls
   sideways instead — and then the header does not stick (an overflow box cannot stick to the window). */
function useHorizontalOverflow(wrapperRef, tableRef) {
  const [overflowing, setOverflowing] = React.useState(false)
  React.useLayoutEffect(() => {
    const wrapper = wrapperRef.current
    const table = tableRef.current
    if (!wrapper || !table || typeof window === "undefined" || typeof window.ResizeObserver !== "function") return undefined
    const measure = () => setOverflowing(table.offsetWidth > wrapper.clientWidth + 1)
    measure()
    const observer = new window.ResizeObserver(measure)
    observer.observe(wrapper)
    observer.observe(table)
    return () => observer.disconnect()
  }, [wrapperRef, tableRef])
  return overflowing
}

function useControlled(value, defaultValue, onChange) {
  const [inner, setInner] = React.useState(defaultValue)
  const controlled = value !== undefined
  const current = controlled ? value : inner
  const set = React.useCallback((next) => {
    if (!controlled) setInner(next)
    onChange?.(next)
  }, [controlled, onChange])
  return [current, set]
}

function toKeyArray(keys) {
  if (!keys) return []
  if (keys instanceof Set) return [...keys]
  return Array.isArray(keys) ? keys : []
}

const SKELETON_WIDTHS = ["72%", "48%", "60%", "84%", "40%", "66%", "54%", "78%"]

/* ------------------------------------------------------------------ rows */

const DataRow = React.memo(function DataRow({
  row,
  rowId,
  index,
  columns,
  selectable,
  canSelect,
  selected,
  expanded,
  focusTarget,
  interactive,
  compact,
  stickyFirst,
  rowLabel,
  className,
  testId,
  checkboxTestId,
  renderExpanded,
  colSpan,
  onRowClick,
  onRowKeyDown,
  onRowFocus,
  onCheckbox,
  onToggleExpanded,
}) {
  const shiftRef = React.useRef(false)
  const ctx = {
    index,
    selected,
    expanded,
    toggleSelected: () => onCheckbox(rowId, !selected, false),
    toggleExpanded: () => onToggleExpanded(rowId),
  }
  // Row states are painted on the cells (sticky cells need an opaque background of their own).
  const cellBase = cn(
    "border-b border-line-2 px-3 align-middle transition-colors first:pl-4 last:pr-4",
    compact ? "h-[38px] py-1" : "h-11 py-1.5",
    selected ? "bg-brand-soft" : cn("bg-surface", interactive && "group-hover/row:bg-surface-2"),
    "group-focus-visible/row:bg-surface-hover first:group-focus-visible/row:shadow-[inset_3px_0_0_var(--brand)]"
  )
  return (
    <>
      <tr
        tabIndex={focusTarget ? 0 : -1}
        data-dt-index={index}
        data-state={selected ? "selected" : undefined}
        aria-expanded={renderExpanded ? expanded : undefined}
        data-testid={testId}
        onClick={(event) => onRowClick(event, rowId)}
        onKeyDown={(event) => onRowKeyDown(event, rowId, index)}
        onFocus={(event) => { if (event.target === event.currentTarget) onRowFocus(index) }}
        className={cn("group/row outline-none", interactive && "cursor-pointer", className)}
      >
        {selectable ? (
          <td
            // The whole 40px cell toggles the row: a near miss of the 16px box must not open the row.
            data-row-click="ignore"
            onClick={canSelect ? (event) => onCheckbox(rowId, !selected, event.shiftKey) : undefined}
            className={cn(cellBase, "w-10 pr-1", canSelect && "cursor-pointer", stickyFirst && "sticky left-0 z-[2]")}
          >
            {canSelect ? (
              <TableCheckbox
                checked={selected}
                aria-label={`Select ${rowLabel}`}
                data-testid={checkboxTestId}
                onClick={(event) => { shiftRef.current = event.shiftKey }}
                onCheckedChange={(next) => {
                  onCheckbox(rowId, next, shiftRef.current)
                  shiftRef.current = false
                }}
              />
            ) : null}
          </td>
        ) : null}
        {columns.map((column, columnIndex) => (
          <td
            key={column.key}
            className={cn(
              cellBase,
              alignClass(column),
              column.truncate && "max-w-0 truncate",
              stickyFirst && columnIndex === 0 && (selectable ? "sticky left-10 z-[2]" : "sticky left-0 z-[2]"),
              typeof column.cellClassName === "function" ? column.cellClassName(row) : column.cellClassName
            )}
          >
            {column.render ? column.render(row, ctx) : <DefaultCell value={row?.[column.key]} />}
          </td>
        ))}
      </tr>
      {renderExpanded && expanded ? (
        <tr data-dt-expanded="true">
          <td colSpan={colSpan} className="border-b border-line-2 bg-surface-2 p-0">
            {renderExpanded(row, ctx)}
          </td>
        </tr>
      ) : null}
    </>
  )
})

function GroupRow({ group, colSpan, collapsed, onToggle, selectable, selectedCount, onSelectGroup, renderGroupHeader, groupActions, groupMeta, groupCount, size, compact }) {
  const total = group.rows.length
  const all = selectable && selectedCount > 0 && selectedCount === group.selectableCount
  const some = selectable && selectedCount > 0 && !all
  // groupCount(group): undefined → the default count pill; null / false → no pill; anything else is shown
  // as muted text after the title (e.g. "30 drawings").
  const count = groupCount ? groupCount(group) : undefined
  const large = size === "lg"
  return (
    <tr data-dt-group={group.key} data-testid="data-table-group">
      <td
        colSpan={colSpan}
        className={cn(
          "border-b border-line bg-surface-2 px-3 pl-4 font-bold",
          large ? "h-10 text-[13px] text-ink" : cn("text-[12px] text-ink-2", compact ? "h-8" : "h-[34px]")
        )}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          {selectable && group.selectableCount ? (
            <TableCheckbox
              checked={all}
              indeterminate={some}
              aria-label={`Select all ${total} in ${typeof group.label === "string" ? group.label : group.key}`}
              onCheckedChange={(next) => onSelectGroup(group, next)}
            />
          ) : null}
          <button
            type="button"
            onClick={() => onToggle(group.key)}
            aria-expanded={!collapsed}
            className="-ml-1 flex min-w-0 items-center gap-1.5 rounded-md px-1 py-0.5 text-left hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronRight className={cn("h-3.5 w-3.5 shrink-0 text-ink-3 transition-transform", !collapsed && "rotate-90")} aria-hidden="true" />
            <span className="min-w-0 truncate">{renderGroupHeader ? renderGroupHeader(group) : (group.label || "No group")}</span>
            {count === undefined ? (
              <span className="shrink-0 rounded-full bg-[color:var(--status-gray-bg)] px-[7px] py-px text-[11px] font-bold tabular-nums text-ink-2">
                {formatNumber(total, { maxDecimals: 0 })}
              </span>
            ) : count === null || count === false ? null : (
              <span className="shrink-0 whitespace-nowrap text-[12px] font-semibold text-ink-3" data-testid="data-table-group-count">{count}</span>
            )}
          </button>
          {groupActions ? groupActions(group) : null}
          {groupMeta ? <div className="ml-auto flex shrink-0 items-center gap-2 text-[12px] font-semibold text-ink-3">{groupMeta(group)}</div> : null}
        </div>
      </td>
    </tr>
  )
}

/* "+ 26 more in LUXOR TOWER" — the compact group more-row (groupMoreVariant="compact"): one link-style button that
   shows the rest of the group. */
function CompactGroupMore({ hidden, label, onAll }) {
  return (
    <div className="px-4 py-1.5">
      <button
        type="button"
        onClick={onAll}
        className="rounded-md text-[12.5px] font-semibold text-[color:var(--brand)] underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        + {formatNumber(hidden, { maxDecimals: 0 })} more{label ? ` in ${label}` : ""}
      </button>
    </div>
  )
}

/* "Showing 100 of 2,000 · Show 100 more · Show all" — under a flat list, or inside an open group. */
function MoreRows({ shown, hidden, step, noun, onMore, onAll }) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 text-[12.5px] text-ink-3">
      <span>
        Showing <b className="font-semibold text-ink-2">{formatNumber(shown, { maxDecimals: 0 })}</b> of{" "}
        <b className="font-semibold text-ink-2">{formatNumber(shown + hidden, { maxDecimals: 0 })}</b>
        {noun ? ` ${noun}` : null}
      </span>
      <span className="flex items-center gap-1.5">
        <Button type="button" variant="outline" size="xs" onClick={onMore}>
          Show {formatNumber(Math.min(step, hidden), { maxDecimals: 0 })} more
        </Button>
        <Button type="button" variant="ghost" size="xs" onClick={onAll}>Show all</Button>
      </span>
    </div>
  )
}

/* ------------------------------------------------------------------ table */

function DataTable({
  columns = [],
  rows = [],
  rowKey = "id",
  columnVisibility,
  sort: sortProp,
  defaultSort = null,
  onSortChange,
  manualSort = false,
  selectable = false,
  selectedKeys,
  defaultSelectedKeys,
  onSelectionChange,
  isRowSelectable,
  keepSelectionOnFilter = false,
  bulkActions,
  bulkMoreActions,
  bulkNoun = "selected",
  bulkTestId,
  renderBulkBar,
  onRowOpen,
  getRowLabel,
  rowClassName,
  rowTestId,
  checkboxTestId,
  headerCheckboxTestId,
  showSortHint = false,
  groupBy,
  sortGroups,
  renderGroupHeader,
  groupActions,
  groupMeta,
  groupCount,
  groupRowSize = "default",
  collapsedGroups: collapsedGroupsProp,
  defaultCollapsedGroups,
  onCollapsedGroupsChange,
  renderExpanded,
  expandedKeys: expandedKeysProp,
  defaultExpandedKeys,
  onExpandedChange,
  density = "default",
  stickyHeader = true,
  stickyOffset = "var(--app-header-height)",
  stickyFirstColumn = false,
  loading = false,
  skeletonRows = 8,
  empty,
  initialRowCount = 100,
  groupRowLimit = 50,
  groupMoreVariant = "default",
  rowCountStep = 100,
  pagingKey,
  caption,
  ariaLabel,
  footer,
  className,
  tableClassName,
  testId = "data-table",
}) {
  const wrapperRef = React.useRef(null)
  const tableRef = React.useRef(null)
  const tbodyRef = React.useRef(null)
  const scrollMode = useHorizontalOverflow(wrapperRef, tableRef)
  const compact = density === "compact"

  // Stable between renders, so a selection or focus change re-renders only the rows it touches.
  const managedColumns = columnVisibility?.visibleColumns
  const visibleColumns = React.useMemo(
    () => managedColumns || columns.filter((column) => !column.defaultHidden),
    [managedColumns, columns]
  )
  const colSpan = visibleColumns.length + (selectable ? 1 : 0)

  /* sort */
  const [sort, setSort] = useControlled(sortProp, defaultSort, onSortChange)
  const sortedRows = React.useMemo(
    () => (manualSort || !sort ? rows : sortRows(rows, sort, sortValueOf(columns))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, sort?.key, sort?.dir, manualSort, columns]
  )

  /* selection */
  const [selectionRaw, setSelection] = useControlled(
    selectedKeys === undefined ? undefined : toKeyArray(selectedKeys),
    toKeyArray(defaultSelectedKeys),
    onSelectionChange
  )
  const selection = React.useMemo(() => new Set(toKeyArray(selectionRaw)), [selectionRaw])
  const keyOf = React.useCallback((row, index) => readKey(row, rowKey, index), [rowKey])
  const allKeys = React.useMemo(() => sortedRows.map((row, index) => keyOf(row, index)), [sortedRows, keyOf])
  const selectableKeys = React.useMemo(
    () => (selectable ? sortedRows.filter((row) => !isRowSelectable || isRowSelectable(row)).map((row) => keyOf(row)) : []),
    [selectable, sortedRows, isRowSelectable, keyOf]
  )

  // The selection only ever holds rows that are in the list (a filter must not leave hidden rows selected).
  React.useEffect(() => {
    if (!selectable || keepSelectionOnFilter || loading || !selection.size) return
    const present = new Set(allKeys)
    const kept = [...selection].filter((key) => present.has(key))
    if (kept.length !== selection.size) setSelection(kept)
  }, [allKeys, selectable, keepSelectionOnFilter, loading, selection, setSelection])

  const selectedCount = selectableKeys.filter((key) => selection.has(key)).length
  const allSelected = selectableKeys.length > 0 && selectedCount === selectableKeys.length
  const someSelected = selectedCount > 0 && !allSelected

  /* groups + expansion */
  const [collapsedRaw, setCollapsed] = useControlled(
    collapsedGroupsProp === undefined ? undefined : toKeyArray(collapsedGroupsProp),
    Array.isArray(defaultCollapsedGroups) ? defaultCollapsedGroups : [],
    onCollapsedGroupsChange
  )
  const collapseAllByDefault = defaultCollapsedGroups === true
  const [openedGroups, setOpenedGroups] = React.useState(() => new Set())
  const collapsed = React.useMemo(() => new Set(toKeyArray(collapsedRaw)), [collapsedRaw])
  const isCollapsed = React.useCallback(
    (key) => (collapseAllByDefault ? !openedGroups.has(key) : collapsed.has(key)),
    [collapseAllByDefault, openedGroups, collapsed]
  )
  const [expandedRaw, setExpanded] = useControlled(
    expandedKeysProp === undefined ? undefined : toKeyArray(expandedKeysProp),
    toKeyArray(defaultExpandedKeys),
    onExpandedChange
  )
  const expanded = React.useMemo(() => new Set(toKeyArray(expandedRaw)), [expandedRaw])

  const groups = React.useMemo(() => {
    if (!groupBy) return null
    const list = groupRows(sortedRows, groupBy)
    return sortGroups ? [...list].sort(sortGroups) : list
  }, [groupBy, sortedRows, sortGroups])

  /* incremental rendering */
  // One row budget for the whole list: the first `limit` rows + a "Showing 100 of 2,000 · Show more"
  // footer. Grouped lists spend the same budget group by group (2,000 rows in 40 open groups still
  // render ~100 rows first), and each open group also stops at `groupRowLimit` rows with a "show more"
  // row inside the group.
  const [limit, setLimit] = React.useState(initialRowCount)
  const [groupLimits, setGroupLimits] = React.useState({})
  React.useEffect(() => {
    setLimit(initialRowCount)
    setGroupLimits({})
  }, [pagingKey, initialRowCount])

  const display = React.useMemo(() => {
    const items = []
    let count = 0
    let more = false
    const pushRow = (row) => {
      const index = count
      count += 1
      items.push({ type: "row", row, key: keyOf(row, index), index })
    }
    if (groups) {
      groups.forEach((group) => {
        if (more) return
        const collapsedGroup = isCollapsed(group.key)
        const cap = groupLimits[group.key] ?? groupRowLimit
        const wanted = collapsedGroup ? 0 : Math.min(cap, group.rows.length)
        // Budget spent: this group and every one after it wait for "Show more" (headers too, so the
        // list never shows an open group without its rows).
        if (wanted > 0 && count >= limit) {
          more = true
          return
        }
        const selectableInGroup = selectable ? group.rows.filter((row) => !isRowSelectable || isRowSelectable(row)) : []
        const groupItem = { ...group, selectableCount: selectableInGroup.length, selectableKeys: selectableInGroup.map((row) => keyOf(row)) }
        items.push({ type: "group", group: groupItem, key: `group:${group.key}` })
        if (collapsedGroup) return
        const take = Math.min(wanted, limit - count)
        group.rows.slice(0, take).forEach(pushRow)
        if (take < wanted) more = true
        else if (group.rows.length > cap) {
          items.push({ type: "groupMore", key: `more:${group.key}`, group: groupItem, shown: cap, hidden: group.rows.length - cap })
        }
      })
    } else {
      sortedRows.slice(0, limit).forEach(pushRow)
      more = sortedRows.length > limit
    }
    return { items, shown: count, hidden: more ? Math.max(0, sortedRows.length - count) : 0 }
  }, [groups, sortedRows, limit, groupLimits, groupRowLimit, keyOf, isCollapsed, selectable, isRowSelectable])

  const showMoreRows = () => setLimit((current) => current + rowCountStep)
  const showAllRows = () => {
    setLimit(Number.POSITIVE_INFINITY)
    if (groups) setGroupLimits(Object.fromEntries(groups.map((group) => [group.key, Number.POSITIVE_INFINITY])))
  }
  // A group's own "show more" also grows the list budget, so the rows below it stay where they were.
  const showMoreInGroup = (item, all) => {
    const added = all ? item.hidden : Math.min(rowCountStep, item.hidden)
    setGroupLimits((current) => ({ ...current, [item.group.key]: all ? Number.POSITIVE_INFINITY : item.shown + rowCountStep }))
    setLimit((current) => current + added)
  }

  /* keyboard focus */
  const [focusIndex, setFocusIndex] = React.useState(0)
  const latest = React.useRef({})
  latest.current = { display, selection, selectableKeys, onRowOpen, setSelection, isRowSelectable, selectable, expanded, setExpanded, rows: sortedRows, keyOf, focusIndex }
  const lastIndexRef = React.useRef(null)

  const rowById = React.useCallback((rowId) => {
    const item = latest.current.display.items.find((entry) => entry.type === "row" && entry.key === rowId)
    return item ? item.row : null
  }, [])

  const toggleRow = React.useCallback((rowId, next, shift) => {
    const state = latest.current
    const current = new Set(state.selection)
    const rowItems = state.display.items.filter((entry) => entry.type === "row")
    const target = rowItems.find((entry) => entry.key === rowId)
    if (shift && lastIndexRef.current !== null && target) {
      const [from, to] = [lastIndexRef.current, target.index].sort((a, b) => a - b)
      rowItems
        .filter((entry) => entry.index >= from && entry.index <= to)
        .filter((entry) => !state.isRowSelectable || state.isRowSelectable(entry.row))
        .forEach((entry) => (next ? current.add(entry.key) : current.delete(entry.key)))
    } else if (next) current.add(rowId)
    else current.delete(rowId)
    if (target) lastIndexRef.current = target.index
    state.setSelection([...current])
  }, [])

  const toggleExpandedRow = React.useCallback((rowId) => {
    const state = latest.current
    const next = new Set(state.expanded)
    if (next.has(rowId)) next.delete(rowId); else next.add(rowId)
    state.setExpanded([...next])
  }, [])

  const focusRowAt = React.useCallback((index) => {
    const node = tbodyRef.current?.querySelector(`tr[data-dt-index="${index}"]`)
    if (node) {
      setFocusIndex(index)
      node.focus()
    }
  }, [])

  const onRowKeyDown = React.useCallback((event, rowId, index) => {
    if (event.target !== event.currentTarget) return
    const state = latest.current
    const last = state.display.shown - 1
    switch (event.key) {
      case "ArrowDown": event.preventDefault(); focusRowAt(Math.min(last, index + 1)); break
      case "ArrowUp": event.preventDefault(); focusRowAt(Math.max(0, index - 1)); break
      case "Home": event.preventDefault(); focusRowAt(0); break
      case "End": event.preventDefault(); focusRowAt(last); break
      case "Enter": {
        const row = rowById(rowId)
        if (row && state.onRowOpen) { event.preventDefault(); state.onRowOpen(row) }
        break
      }
      case " ": {
        const row = rowById(rowId)
        if (row && state.selectable && (!state.isRowSelectable || state.isRowSelectable(row))) {
          event.preventDefault()
          toggleRow(rowId, !state.selection.has(rowId), event.shiftKey)
        }
        break
      }
      default:
    }
  }, [focusRowAt, rowById, toggleRow])

  const onRowClick = React.useCallback((event, rowId) => {
    // React events bubble through portals: a click inside a popover, dialog or select opened from a
    // cell reaches the row although it is not in it. Only clicks inside the row itself count.
    if (!event.currentTarget.contains(event.target)) return
    const interactiveTarget = event.target.closest?.(INTERACTIVE_SELECTOR)
    if (interactiveTarget && event.currentTarget.contains(interactiveTarget) && interactiveTarget !== event.currentTarget) return
    if (window.getSelection && String(window.getSelection()).length > 0) return // selecting text, not opening
    const state = latest.current
    const row = rowById(rowId)
    if (!row) return
    if (state.onRowOpen) state.onRowOpen(row)
    else if (state.selectable && (!state.isRowSelectable || state.isRowSelectable(row))) toggleRow(rowId, !state.selection.has(rowId), event.shiftKey)
  }, [rowById, toggleRow])

  const onRowFocus = React.useCallback((index) => setFocusIndex(index), [])

  const toggleAll = (next) => {
    const current = new Set(selection)
    selectableKeys.forEach((key) => (next ? current.add(key) : current.delete(key)))
    setSelection([...current])
  }

  const onSelectGroup = (group, next) => {
    const current = new Set(selection)
    group.selectableKeys.forEach((key) => (next ? current.add(key) : current.delete(key)))
    setSelection([...current])
  }

  const onToggleGroup = (key) => {
    if (collapseAllByDefault) {
      setOpenedGroups((current) => {
        const next = new Set(current)
        if (next.has(key)) next.delete(key); else next.add(key)
        return next
      })
      return
    }
    const next = new Set(collapsed)
    if (next.has(key)) next.delete(key); else next.add(key)
    setCollapsed([...next])
  }

  const clearSelection = () => setSelection([])
  // Clearing from the bar (Escape, ×) removes the bar: put keyboard focus back on the current row.
  const focusCurrentRow = React.useCallback(() => {
    const { display: current, focusIndex: index } = latest.current
    if (current.shown > 0) focusRowAt(Math.min(index, current.shown - 1))
  }, [focusRowAt])
  const selectedRows = React.useMemo(
    () => (selection.size ? sortedRows.filter((row) => selection.has(keyOf(row))) : []),
    [selection, sortedRows, keyOf]
  )

  const total = sortedRows.length
  const hiddenCount = display.hidden
  const showEmpty = !loading && total === 0
  const hasMore = !loading && hiddenCount > 0
  const nothingBelow = !footer && !hasMore
  const thClass = cn(
    "h-9 whitespace-nowrap border-b border-line bg-surface-2 px-3 text-left text-[11.5px] font-bold tracking-[0.02em] text-ink-3 first:rounded-tl-card first:pl-4 last:rounded-tr-card last:pr-4",
    stickyHeader && "sticky top-[var(--dt-sticky-top)] z-[6]"
  )
  const label = (row) => (getRowLabel ? getRowLabel(row) : "row")
  const resolveActions = (actions) => (typeof actions === "function" ? actions(selectedRows) : actions || [])

  return (
    <div className={cn("min-w-0", className)} data-testid={testId}>
      <div
        ref={wrapperRef}
        // `relative`: absolutely positioned text in cells (sr-only spans) is clipped by this box instead of widening the page.
        className={cn("relative min-w-0 rounded-card border border-line bg-surface shadow-card", scrollMode && "overflow-x-auto")}
        data-scroll={scrollMode ? "x" : "none"}
        // Window-sticky header under the app top bar; 0 while the table scrolls sideways (its own box).
        style={{ "--dt-sticky-top": scrollMode ? "0px" : stickyOffset }}
      >
        <table
          ref={tableRef}
          aria-label={ariaLabel}
          aria-busy={loading || undefined}
          className={cn("w-full border-separate border-spacing-0 text-[13px] text-ink", tableClassName)}
        >
          {caption ? <caption className="sr-only">{caption}</caption> : null}
          <colgroup>
            {selectable ? <col style={{ width: 40 }} /> : null}
            {visibleColumns.map((column) => (
              <col key={column.key} style={{ width: column.width }} />
            ))}
          </colgroup>
          <thead>
            <tr>
              {selectable ? (
                <th scope="col" className={cn(thClass, "w-10 pr-1", stickyFirstColumn && "left-0 z-[7]", stickyFirstColumn && !stickyHeader && "sticky")}>
                  <TableCheckbox
                    checked={allSelected}
                    indeterminate={someSelected}
                    disabled={!selectableKeys.length}
                    onCheckedChange={toggleAll}
                    aria-label={allSelected ? "Clear selection" : `Select all ${formatNumber(selectableKeys.length, { maxDecimals: 0 })} rows`}
                    data-testid={headerCheckboxTestId}
                  />
                </th>
              ) : null}
              {visibleColumns.map((column, columnIndex) => {
                const active = sort && sort.key === column.key
                const align = alignOf(column)
                const sticky = stickyFirstColumn && columnIndex === 0
                return (
                  <th
                    key={column.key}
                    scope="col"
                    // min-width works on cells (a <col> ignores it); the column cannot shrink below it.
                    style={column.minWidth ? { minWidth: column.minWidth } : undefined}
                    aria-sort={column.sortable ? (active ? (sort.dir === "desc" ? "descending" : "ascending") : "none") : undefined}
                    className={cn(
                      thClass,
                      alignClass(column),
                      sticky && (selectable ? "left-10 z-[7]" : "left-0 z-[7]"),
                      sticky && !stickyHeader && "sticky",
                      column.headerClassName
                    )}
                  >
                    {column.sortable ? (
                      <button
                        type="button"
                        onClick={() => setSort(nextSort(sort, column.key, column.defaultSortDir || "asc"))}
                        className={cn(
                          "group/sort -mx-1 inline-flex items-center gap-1 rounded px-1 py-0.5 hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                          active && "text-ink",
                          align === "right" && "flex-row-reverse"
                        )}
                      >
                        <span>{column.header}</span>
                        {active ? (
                          sort.dir === "desc" ? <ArrowDown className="h-3 w-3" aria-hidden="true" /> : <ArrowUp className="h-3 w-3" aria-hidden="true" />
                        ) : (
                          <ChevronsUpDown
                            className={cn(
                              "h-3 w-3 text-ink-4 transition-opacity",
                              showSortHint || column.sortHint ? "opacity-100" : "opacity-0 group-hover/sort:opacity-100 group-focus-visible/sort:opacity-100"
                            )}
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    ) : column.header}
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody
            ref={tbodyRef}
            className={cn(
              "[&>tr:last-child>td]:border-b-0",
              nothingBelow && "[&>tr:last-child>td:first-child]:rounded-bl-card [&>tr:last-child>td:last-child]:rounded-br-card"
            )}
          >
            {loading ? (
              Array.from({ length: skeletonRows }, (_, rowIndex) => (
                <tr key={`skeleton-${rowIndex}`} aria-hidden="true" data-testid="data-table-skeleton-row">
                  {selectable ? (
                    <td className={cn("h-11 border-b border-line-2 pl-4 pr-1", compact && "h-[38px]")}>
                      <span className="ui-skeleton-shimmer block h-4 w-4 rounded" />
                    </td>
                  ) : null}
                  {visibleColumns.map((column, columnIndex) => (
                    <td key={column.key} className={cn("border-b border-line-2 px-3 first:pl-4 last:pr-4", compact ? "h-[38px]" : "h-11")}>
                      <span
                        className={cn("ui-skeleton-shimmer block h-2.5 rounded-full", alignOf(column) === "right" && "ml-auto")}
                        style={{ width: alignOf(column) === "right" ? "48%" : SKELETON_WIDTHS[(rowIndex + columnIndex) % SKELETON_WIDTHS.length] }}
                      />
                    </td>
                  ))}
                </tr>
              ))
            ) : showEmpty ? (
              <tr>
                <td colSpan={colSpan} className="p-0">
                  {empty || <EmptyState title="Nothing to show yet" text="Rows appear here as soon as there is data." />}
                </td>
              </tr>
            ) : (
              display.items.map((item) => (item.type === "groupMore" ? (
                <tr key={item.key} data-testid="data-table-group-more">
                  <td colSpan={colSpan} className="border-b border-line-2 bg-surface p-0">
                    {groupMoreVariant === "compact" ? (
                      <CompactGroupMore
                        hidden={item.hidden}
                        label={typeof item.group.label === "string" ? item.group.label : ""}
                        onAll={() => showMoreInGroup(item, true)}
                      />
                    ) : (
                      <MoreRows
                        shown={item.shown}
                        hidden={item.hidden}
                        step={rowCountStep}
                        noun={`in ${typeof item.group.label === "string" && item.group.label ? item.group.label : "this group"}`}
                        onMore={() => showMoreInGroup(item, false)}
                        onAll={() => showMoreInGroup(item, true)}
                      />
                    )}
                  </td>
                </tr>
              ) : item.type === "group" ? (
                <GroupRow
                  key={item.key}
                  group={item.group}
                  colSpan={colSpan}
                  compact={compact}
                  collapsed={isCollapsed(item.group.key)}
                  onToggle={onToggleGroup}
                  selectable={selectable}
                  selectedCount={item.group.selectableKeys.filter((key) => selection.has(key)).length}
                  onSelectGroup={onSelectGroup}
                  renderGroupHeader={renderGroupHeader}
                  groupActions={groupActions}
                  groupMeta={groupMeta}
                  groupCount={groupCount}
                  size={groupRowSize}
                />
              ) : (
                <DataRow
                  key={item.key}
                  row={item.row}
                  rowId={item.key}
                  index={item.index}
                  columns={visibleColumns}
                  selectable={selectable}
                  canSelect={selectable && (!isRowSelectable || isRowSelectable(item.row))}
                  selected={selection.has(item.key)}
                  expanded={expanded.has(item.key)}
                  focusTarget={item.index === Math.min(focusIndex, display.shown - 1)}
                  interactive={Boolean(onRowOpen) || selectable}
                  compact={compact}
                  stickyFirst={stickyFirstColumn}
                  rowLabel={label(item.row)}
                  className={typeof rowClassName === "function" ? rowClassName(item.row) : rowClassName}
                  testId={typeof rowTestId === "function" ? rowTestId(item.row) : rowTestId}
                  checkboxTestId={typeof checkboxTestId === "function" ? checkboxTestId(item.row) : checkboxTestId}
                  renderExpanded={renderExpanded}
                  colSpan={colSpan}
                  onRowClick={onRowClick}
                  onRowKeyDown={onRowKeyDown}
                  onRowFocus={onRowFocus}
                  onCheckbox={toggleRow}
                  onToggleExpanded={toggleExpandedRow}
                />
              )))
            )}
          </tbody>
        </table>
        {hasMore ? (
          <div className="rounded-b-card border-t border-line-2" data-testid={`${testId}-more`}>
            <MoreRows
              shown={display.shown}
              hidden={hiddenCount}
              step={rowCountStep}
              onMore={showMoreRows}
              onAll={showAllRows}
            />
          </div>
        ) : null}
        {footer}
      </div>
      {selectable ? (
        renderBulkBar ? renderBulkBar({ count: selection.size, selectedRows, clear: clearSelection, returnFocus: focusCurrentRow }) : (bulkActions || bulkMoreActions) ? (
          <BulkBar
            count={selection.size}
            noun={bulkNoun}
            onClear={clearSelection}
            returnFocus={focusCurrentRow}
            actions={resolveActions(bulkActions)}
            moreActions={resolveActions(bulkMoreActions)}
            testId={bulkTestId || `${testId}-bulkbar`}
          />
        ) : null
      ) : null}
    </div>
  )
}

/* ------------------------------------------------------------------ columns menu */

/*
  ColumnsMenu — "Columns 7/15" toolbar button for a useColumnVisibility() result: one check box per
  column (columns that cannot be hidden show as locked), "Show all" and "Reset". showCount={false} gives a plain
  "Columns" button.
*/
function ColumnsMenu({ state, label = "Columns", align = "end", showCount = true, className, testId = "columns-menu" }) {
  if (!state) return null
  const counted = showCount && state.totalCount > 0
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className={cn("text-ink-2", className)} data-testid={testId}>
          <Columns3 aria-hidden="true" />
          {label}
          {counted ? <span className="tabular-nums text-ink-3">{state.visibleCount}/{state.totalCount}</span> : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align={align} className="w-64 rounded-xl border-line bg-surface p-1.5 text-ink shadow-overlay">
        <p className="px-2.5 pb-1 pt-1.5 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">Show columns</p>
        <div className="max-h-80 overflow-y-auto" role="group" aria-label="Columns">
          {state.menuColumns.map((entry) => {
            const id = `${testId}-${entry.key}`
            return (
              <label
                key={entry.key}
                htmlFor={id}
                className={cn("flex h-8 items-center gap-2.5 rounded-lg px-2.5 text-[13px]", entry.hideable ? "cursor-pointer hover:bg-surface-hover" : "text-ink-3")}
              >
                <TableCheckbox
                  id={id}
                  checked={entry.visible}
                  disabled={!entry.hideable}
                  onCheckedChange={(next) => state.setVisible(entry.key, next)}
                />
                <span className="min-w-0 flex-1 truncate">{entry.label}</span>
                {!entry.hideable ? <span className="text-[11px] font-medium text-ink-3">Always</span> : null}
              </label>
            )
          })}
        </div>
        <div className="mt-1 flex items-center justify-between border-t border-line-2 px-1 pt-1.5">
          <Button type="button" variant="ghost" size="xs" onClick={state.showAll} disabled={state.visibleCount === state.totalCount}>Show all</Button>
          <Button type="button" variant="ghost" size="xs" onClick={state.reset} disabled={!state.isCustomized}>
            <RotateCcw aria-hidden="true" />
            Reset
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  )
}

export { DataTable, ColumnsMenu }
